import type { ComboboxOptionType } from '@zillow/constellation-10';
import { FormField, FormHelp, Label, Layout, Paragraph } from '@zillow/constellation-10';
import { TimeZoneDropdownSelect, TourDurationDropdownSelect } from '@zg-rentals/touring';
import type { TimeZoneIds } from '@zg-rentals/touring';
import { WizardBody, WizardFormSectionContainer, WizardHeading } from '../../../../styles/wizard';
import type { TourDuration } from '@zg-rentals/ts-z-calendar-client';
import { useSelector } from '../../../../selectors/useSelector';
import accessUtils from '../../../../utils/accessUtils';

type TourSchedulingTimeZoneBodyProps = {
  selectedTimeZone?: TimeZoneIds;
  onSelectTimeZone: (option: ComboboxOptionType) => void;
  selectedTourDuration?: TourDuration;
  onSelectTourDuration: (option: ComboboxOptionType) => void;
};

const TourSchedulingTimeZoneBody = ({
  selectedTimeZone,
  onSelectTimeZone,
  selectedTourDuration,
  onSelectTourDuration,
}: TourSchedulingTimeZoneBodyProps) => {
  const appConfig = useSelector((state) => state.app.appConfig);
  const canAccessPostingPathTourDuration = accessUtils.canAccessPostingPathTourDuration({ appConfig });
  return (
    <div data-testid="TourSchedulingTimeZoneBody">
      <WizardFormSectionContainer>
        {canAccessPostingPathTourDuration ? (
          <WizardHeading level={1} textStyle="heading-sm" data-testid="TourSchedulingTimeZoneBody-Heading">
            Tour settings
          </WizardHeading>
        ) : (
          <>
            <WizardHeading level={1} textStyle="heading-sm" data-testid="TourSchedulingTimeZoneBody-Heading">
              Select the property time zone
            </WizardHeading>
            <WizardBody>
              <Layout.Root>
                <Layout.Content css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' }, padding: 'zero' }}>
                  <Paragraph data-testid="TourSchedulingTimeZoneBody-Description">
                    This helps ensure that your tour availability matches what&apos;s provided to interested renters.
                  </Paragraph>
                </Layout.Content>
                <Layout.Content
                  css={{ gridColumnEnd: { base: 'span 0', md: 'span 4' }, padding: 'zero' }}
                ></Layout.Content>
              </Layout.Root>
            </WizardBody>
          </>
        )}

        <Layout.Root>
          {canAccessPostingPathTourDuration ? (
            <Layout.Content
              css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' }, padding: 'zero', minWidth: '400px' }}
            >
              <FormField
                label={<Label size="lg">Length of each tour</Label>}
                control={
                  <TourDurationDropdownSelect
                    tourDuration={selectedTourDuration}
                    handleOptionSelect={onSelectTourDuration}
                  />
                }
                required={true}
                css={{ marginBottom: 'tight' }}
              />
              <FormField
                label={<Label size="lg">Property time zone</Label>}
                description={
                  <FormHelp>
                    This helps ensure that your tour availability matches what&apos;s provided to interested renters.
                  </FormHelp>
                }
                control={
                  <TimeZoneDropdownSelect
                    timeZone={selectedTimeZone}
                    handleOptionSelect={onSelectTimeZone}
                    disableTimeZoneSelection={false}
                  />
                }
                required={true}
                css={{ marginBottom: 'tight' }}
              />
            </Layout.Content>
          ) : (
            <Layout.Content css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' }, padding: 'zero' }}>
              <FormField
                label={<Label size="lg">Property time zone</Label>}
                control={
                  <TimeZoneDropdownSelect
                    timeZone={selectedTimeZone}
                    handleOptionSelect={onSelectTimeZone}
                    disableTimeZoneSelection={false}
                  />
                }
                required={true}
                css={{ marginBottom: 'tight' }}
              />
            </Layout.Content>
          )}
          <Layout.Content css={{ gridColumnEnd: { base: 'span 0', md: 'span 4' }, padding: 'zero' }}></Layout.Content>
        </Layout.Root>
      </WizardFormSectionContainer>
    </div>
  );
};

export default TourSchedulingTimeZoneBody;
